import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RegisterFormData } from '../../types';
import { EducationLevelSelect } from '../form-fields';
import Checkbox from '@/components/common/Checkbox';
import { useAuthStore } from '@/store';

// Policy Dialog Component
const UsagePolicyDialog = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl p-6 overflow-y-auto max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="mt-2">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">سياسة الاستخدام</h2>
            </div>

            <div className="text-gray-700 leading-relaxed">
              {/* Introduction */}
              <p className="mb-6">
                يُرجى قراءة سياسة الاستخدام بعناية، حيث أن عملية تسجيلك في المنصة يعني موافقتك على جميع البنود أدناه.
              </p>

              {/* Section 1 */}
              <section className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">١. نطاق الاستخدام</h3>
                <div className="space-y-3 pr-6">
                  <p className="flex">
                    <span className="ml-2">•</span>
                    هذه المنصة مخصصة فقط لأفراد عائلة آل حمود الهاجري، ولا يحق لأي شخص من خارج العائلة التسجيل أو استخدام أي من خدماتها.
                  </p>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يحتفظ مشرفو المنصة بحق رفض أو إلغاء أي تسجيل لا يتوافق مع هذه السياسة.
                  </p>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يحق لمشرفي المنصة تغيير عضوية المستخدم من مستخدم عام إلى مستخدم بصلاحيات مشرف أو العكس في أي وقت، وذلك وفقًا لمصالح، أو سياسة، أو عمل اللجنة المشرفة على المنصة.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">٢. جمع وحفظ المعلومات</h3>
                <div className="space-y-3 pr-6">
                  <p className="flex">
                    <span className="ml-2">•</span>
                    عند استخدامك للمنصة، فإنك توافق على جمع وتخزين البيانات الشخصية التي تقدمها، وذلك بهدف حفظ معلومات العائلة في الأرشيف العائلي.
                  </p>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يتم الاحتفاظ بالبيانات بسرية، ولا يتم مشاركتها مع أي جهة خارجية.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">٣. الخدمات المتاحة</h3>
                <p className="mb-3">يمكن لأفراد العائلة استخدام الخدمات التالية:</p>
                <div className="space-y-3 pr-6">
                  <p className="flex">
                    <span className="ml-2">•</span>
                    استعراض وتصفح شجرة العائلة.
                  </p>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    تقديم طلبات مثل:
                  </p>
                  <div className="space-y-2 pr-6">
                    <p className="flex">
                      <span className="ml-2">•</span>
                      إضافة مولود جديد.
                    </p>
                    <p className="flex">
                      <span className="ml-2">•</span>
                      خدمة "إضافة من أنت"، لحفظ بيانات الأفراد في الأرشيف العائلي.
                    </p>
                  </div>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    الاطلاع على الأرشيف العائلي، والذي يحتوي على بيانات أفراد العائلة المسجلة.
                  </p>
                </div>
              </section>

              {/* Section 4 */}
              <section className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">٤. المسؤولية والاستخدام العادل</h3>
                <div className="space-y-3 pr-6">
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يُمنع إدخال بيانات غير صحيحة أو مضللة، وسيتم حذف أي معلومات لا تتطابق مع الحقيقة.
                  </p>
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يُمنع مشاركة بيانات الدخول الخاصة بالمنصة مع غير أفراد العائلة، أو استخدامها لأغراض غير مخصصة لها.
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">٥. التعديلات على السياسة</h3>
                <div className="pr-6">
                  <p className="flex">
                    <span className="ml-2">•</span>
                    يحق لإدارة المنصة تعديل هذه السياسة في أي وقت.
                  </p>
                </div>
              </section>

              {/* Agreement Notice */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-700">
                  بالنقر على "الموافقة" أثناء التسجيل، فأنت تقر بموافقتك الكاملة على هذه السياسة وجميع البنود المذكورة أعلاه
                </p>
              </div>

            </div>
          </div>
          

        </div>
      </div>
    </div>
  );
};

interface AdditionalInfoProps {
  form: UseFormReturn<RegisterFormData>;
}

export function AdditionalInfo({ form }: AdditionalInfoProps) {
  const { register, formState: { errors }, watch, setValue } = form;
  const [isOpen, setIsOpen] = useState(false);
  const educationLevel = watch('educationLevel');
  const otherEducationLevel = watch('otherEducationLevel');

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  const handleEducationLevelChange = (value: string) => {
    setValue('educationLevel', value, { shouldValidate: true });
    if (value !== 'other') {
      setValue('otherEducationLevel', '');
    }
  };

  return (
    <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="region">المنطقة *</Label>
          <Input
            id="region"
            {...register('region', { required: 'هذا الحقل مطلوب' })}
            placeholder="أدخل المنطقة"
          />
          {errors.region && (
            <p className="text-sm text-red-500">{errors.region.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="occupation">المهنة *</Label>
          <Input
            id="occupation"
            {...register('occupation', { required: 'هذا الحقل مطلوب' })}
            placeholder="المهنة"
          />
          {errors.occupation && (
            <p className="text-sm text-red-500">{errors.occupation.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="branch">الفرع *</Label>
          <Input
            id="branch"
            {...register('branch', { required: 'هذا الحقل مطلوب' })}
            placeholder="مثل: حمود / راشد"
          />
          {errors.branch && (
            <p className="text-sm text-red-500">{errors.branch.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>المرحلة الدراسية *</Label>
          <EducationLevelSelect
            value={educationLevel}
            onChange={handleEducationLevelChange}
            error={errors.educationLevel?.message || (educationLevel === 'other' && errors.otherEducationLevel?.message)}
            otherValue={otherEducationLevel}
            onOtherChange={(value) => setValue('otherEducationLevel', value, { shouldValidate: true })}
          />
        </div>
      </div>

      {/* Account Credentials Section */}
      <div className="space-y-6 pt-6 border-t">
        <h3 className="text-lg font-semibold">بيانات تسجيل الدخول</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">رقم الجوال *</Label>
            <Input
              id="phoneNumber"
              type="tel"
              dir="ltr"
              className="text-left"
              {...register('phoneNumber', {
                required: 'رقم الجوال مطلوب',
                validate: (value) => {
                  if (!/^(009665\d{8}|00965[569]\d{7})$/.test(value)) {
                    return 'رقم الجوال غير صحيح';
                  }
                  return true;
                }
              })}
              placeholder="009665xxxxxxxx, 009659xxxxxxx"
            />
            {errors.phoneNumber && (
              <p className="text-sm text-red-500">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">كلمة المرور *</Label>
            <Input
              id="password"
              type="password"
              {...register('password', {
                required: 'كلمة المرور مطلوبة',
                minLength: {
                  value: 4,
                  message: 'يجب أن تحتوي كلمة المرور على 4 أرقام'
                },
                validate: (value) => {
                  if (!/[a-zA-Z0-9]$/.test(value)) {
                    return 'يجب أن تحتوي كلمة المرور على أرقام فقط';
                  }
                  return true;
                }
              })}
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">تأكيد كلمة المرور *</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword', {
                required: 'تأكيد كلمة المرور مطلوب',
                validate: (value) => 
                  value === watch('password') || 'كلمة المرور غير متطابقة'
              })}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* سياسة الاستخدام */}
      {!isAdmin && (<div className="space-y-2">
        <div className="flex items-center">
          <Checkbox
            id="acceptTerms"
            label={
              <>
                أوافق على{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(true);
                  }}
                  className="text-primary hover:underline"
                >
                  سياسة الاستخدام
                </button>
              </>
            }
            {...register('acceptTerms', { required: 'يجب الموافقة على سياسة الاستخدام'})}
          />
        </div>
        {errors.acceptTerms && (
          <p className="text-sm text-red-500">{errors.acceptTerms.message}</p>
        )}
      </div>)}

      {/* Policy Dialog */}

      <UsagePolicyDialog 
          isOpen={isOpen} 
          onClose={() => setIsOpen(false)} 
      />
    </div>
  );
}